/**
 * GlobalSearch (top-bar) — always-present jump-to-a-file search. The SAME server
 * search as All Clients (GET /leads/search): name / phone digits / email / job#
 * / address, exact job numbers first. Debounced dropdown, 8 results (name · job#
 * · stage) and the exact total, with a line to All Clients for the full set.
 * Keyboard: ↑/↓ navigate, Enter opens, Escape closes. Company-scoped — open to
 * every employee (shared files).
 */

import React, { useEffect, useRef, useState } from 'react';
import { useLeads } from '../contexts/LeadsContext';
import { useApiClient } from '../hooks/useApi';
import { formatJobNo } from '../lib/jobNumber';
import { stageColor, stageBg } from '../lib/stages';
import { Icons } from '../lib/icons';

const MAX = 8;

export function GlobalSearch({ onSelectLead, onSeeAll, fullWidth, autoFocus }: {
  onSelectLead: (leadId: string) => void;
  onSeeAll: (query: string) => void;
  /** Phone top bar: the box fills its row instead of the 280px desktop width. */
  fullWidth?: boolean;
  autoFocus?: boolean;
}) {
  // `version` (contexts/LeadsContext) bumps when any lead changes. This lives in
  // the persistent top bar: without it, a lead created after login stayed
  // invisible here until a reload.
  const { version } = useLeads();
  const api = useApiClient();

  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 150);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => setActive(0), [debounced]);

  // Close when clicking outside the search box.
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const query = debounced.trim();
  // The server pins exact job-number hits to the top: the dropdown shows only
  // MAX, and a 4-digit job number also matches any phone containing that run.
  const [results, setResults] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  useEffect(() => {
    if (query.length < 2) { setResults([]); setTotal(0); return; }
    let alive = true; // a slower, older response must not replace a newer one
    api.searchLeads({ q: query, page: 1, page_size: MAX })
      .then((r) => { if (alive) { setResults(r.data?.rows ?? []); setTotal(r.data?.total ?? 0); } })
      .catch(() => { if (alive) { setResults([]); setTotal(0); } });
    return () => { alive = false; };
  }, [api, query, version]);
  const showDropdown = open && query.length >= 2;

  const openLead = (id: string) => {
    setOpen(false);
    setQ('');
    onSelectLead(id);
  };

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setOpen(false);
      e.currentTarget.blur();
      return;
    }
    if (!results.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const r = results[active];
      if (r) openLead(r.id);
    }
  };

  return (
    <div ref={boxRef} style={{ position: 'relative', width: fullWidth ? '100%' : '280px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'var(--crm-search-bg)', border: `1px solid ${showDropdown ? 'var(--crm-focus)' : 'var(--crm-search-line)'}`, borderRadius: '7px', padding: '5px 9px' }}>
        <Icons.search size={14} aria-hidden="true" style={{ color: 'var(--crm-dim)', flexShrink: 0 }} />
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder="Search clients…"
          aria-label="Search clients"
          autoFocus={autoFocus}
          style={{ flex: 1, minWidth: 0, background: 'transparent', border: 'none', outline: 'none', color: 'var(--crm-search-text)', fontSize: '12px' }}
        />
      </div>

      {showDropdown && (
        <div style={{ position: 'absolute', top: '38px', left: 0, right: 0, background: 'var(--crm-panel)', border: '1px solid var(--crm-line)', borderRadius: '8px', boxShadow: '0 8px 24px var(--crm-shadow-color)', overflow: 'hidden', zIndex: 1000 }}>
          {results.length === 0 ? (
            <div style={{ padding: '12px', fontSize: '12px', color: 'var(--crm-dim)', textAlign: 'center' }}>No matching clients.</div>
          ) : (
            results.map((l, i) => {
              const name = `${l.first_name ?? ''} ${l.last_name ?? ''}`.trim() || 'Unnamed';
              const color = stageColor(l.stage);
              return (
                <div
                  key={l.id}
                  onMouseDown={(e) => { e.preventDefault(); openLead(l.id); }}
                  onMouseEnter={() => setActive(i)}
                  style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 11px', cursor: 'pointer', background: i === active ? 'var(--crm-panel-2)' : 'transparent', borderBottom: '1px solid var(--crm-panel-2)' }}
                >
                  <span style={{ fontFamily: "'DM Mono', monospace", fontSize: '11px', color: 'var(--crm-dim)', flexShrink: 0 }}>{formatJobNo(l)}</span>
                  <span style={{ flex: 1, minWidth: 0, fontSize: '12px', color: 'var(--crm-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
                  <span style={{ flexShrink: 0, fontSize: '10px', color, background: stageBg(l.stage), padding: '1px 7px', borderRadius: '4px' }}>{l.stage}</span>
                </div>
              );
            })
          )}
          {total > results.length && (
            <div
              onMouseDown={(e) => { e.preventDefault(); const carry = query; setOpen(false); setQ(''); onSeeAll(carry); }}
              style={{ padding: '8px 11px', fontSize: '11px', color: 'var(--crm-accent)', cursor: 'pointer', textAlign: 'center' }}
            >
              See all {total.toLocaleString()} results in All Clients →
            </div>
          )}
        </div>
      )}
    </div>
  );
}
